import type {
  CampaignPlacement,
  ComplianceStatus,
  StoreProfileDetail,
  StoreImage,
} from '../types';
import {
  buildSpatialEntities,
  resolveCampaignSpatialEntity,
  resolveImageSpatialEntity,
  resolveMediaSpatialEntity,
  resolveWalkthroughSpatialEntities,
  type SpatialEntity,
} from './spatialEntity';
import { resolveEvidenceImageUrl } from './evidenceImageUrl';
import { getInstallImagesForInstoreLocation } from './instoreLocationGroups';
import {
  isRetailerCampaign,
  resolveCampaignsWithPhases,
} from './campaignBrowseUtils';

export type RetailerDestination = 'map' | 'details' | 'campaigns' | 'history';

export type TimelineMode = 'current' | 'previous_visit' | 'previous_campaign' | 'custom';

export type RetailerStoreStatus = 'healthy' | 'attention' | 'setup_needed';

export interface RetailerSummaryCard {
  id: 'campaigns' | 'issues' | 'visit';
  title: string;
  value: string;
  detail: string;
  tone: 'neutral' | 'positive' | 'warning';
}

export interface RetailerIssue {
  id: string;
  title: string;
  detail: string;
  locationLabel: string;
  severity: 'warning' | 'critical';
  spatialEntity?: SpatialEntity;
}

export interface HistoryEntry {
  id: string;
  date: string;
  title: string;
  description: string;
  kind: 'visit' | 'campaign' | 'change' | 'evidence';
}

export interface MapPoint {
  id: string;
  kind: 'campaign' | 'photo' | 'video' | 'media' | 'issue';
  x: number;
  y: number;
  label: string;
  spatialEntityId: string;
  spatialEntityKind: SpatialEntity['kind'];
  /** Representative imagery turns map dots into visual store moments. */
  previewImageUrl?: string | null;
  /** Number of same-kind records grouped into this marker. */
  count?: number;
}

export function resolveMapPointLayerColor(
  point: Pick<MapPoint, 'spatialEntityId' | 'spatialEntityKind'>,
  zoneOutlineColors: Record<string, string>,
  fallback: string,
): string {
  if (point.spatialEntityKind !== 'zone') return fallback;
  return zoneOutlineColors[point.spatialEntityId] ?? fallback;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isSameDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

const COMPLIANCE_ISSUES: ComplianceStatus[] = ['attention_required', 'non_compliant', 'pending'];

function parseIsoDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function resolveTimelineDate(
  profile: StoreProfileDetail,
  mode: TimelineMode,
  customDate: string | null,
): Date | null {
  switch (mode) {
    case 'current':
      return new Date();
    case 'previous_visit': {
      const surveyDates = profile.surveys.map((survey) => survey.surveyDate).sort();
      const walkDates = profile.walkthroughs.map((walk) => walk.captureDate).sort();
      const combined = [...surveyDates, ...walkDates];
      if (combined.length === 0) return null;
      if (combined.length === 1) return parseIsoDate(combined[0]);
      return parseIsoDate(combined[combined.length - 1] ?? null);
    }
    case 'previous_campaign': {
      const today = new Date();
      const resolved = resolveCampaignsWithPhases(profile.campaigns, today);
      const previousDates = resolved
        .filter((campaign) => campaign.phase === 'previous')
        .map((campaign) => campaign.latestImageDate ?? campaign.installationDate)
        .filter((date) => date && date !== '—')
        .sort();
      return parseIsoDate(previousDates[previousDates.length - 1] ?? null);
    }
    case 'custom':
      return parseIsoDate(customDate);
    default: {
      const _exhaustive: never = mode;
      return _exhaustive;
    }
  }
}

export function filterCampaignsByDate(
  campaigns: CampaignPlacement[],
  asOf: Date | null,
): CampaignPlacement[] {
  const reference = asOf ?? new Date();
  const resolved = resolveCampaignsWithPhases(campaigns, reference);
  if (!asOf) return resolved;

  const asOfTime = startOfDay(asOf).getTime();
  return resolved.filter((campaign) => {
    const install = parseIsoDate(campaign.installationDate);
    if (!install) return true;
    return startOfDay(install).getTime() <= asOfTime;
  });
}

export function getRetailerStoreStatus(profile: StoreProfileDetail): RetailerStoreStatus {
  const issueCount = countOpenIssues(profile).length;
  if (issueCount > 0) return 'attention';
  if (profile.twinReadiness.state === 'published') return 'healthy';
  if (profile.floorplanImageUrl && profile.floorplanZones.length + profile.aisles.length > 0) return 'healthy';
  return 'setup_needed';
}

export function retailerStatusLabel(status: RetailerStoreStatus): string {
  switch (status) {
    case 'healthy':
      return 'Up to date';
    case 'attention':
      return 'Needs attention';
    case 'setup_needed':
      return 'Store tour in progress';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function countOpenIssues(profile: StoreProfileDetail): RetailerIssue[] {
  const entities = buildSpatialEntities(profile);
  const issues: RetailerIssue[] = [];

  for (const campaign of profile.campaigns) {
    if (!isRetailerCampaign(campaign)) continue;
    if (campaign.phase !== 'current') continue;
    if (campaign.complianceStatus === 'compliant') continue;
    if (!COMPLIANCE_ISSUES.includes(campaign.complianceStatus)) continue;
    const spatial = resolveCampaignSpatialEntity(campaign, entities, profile.floorplanZones, profile.aisles);
    issues.push({
      id: `campaign-${campaign.id}`,
      title: campaign.campaignName,
      detail: campaign.issues?.[0] ?? `Status: ${campaign.complianceStatus.replace(/_/g, ' ')}`,
      locationLabel: spatial?.label ?? campaign.instoreLocationName ?? 'Store',
      severity: campaign.complianceStatus === 'non_compliant' ? 'critical' : 'warning',
      spatialEntity: spatial,
    });
  }

  for (const media of profile.mediaLocations) {
    if (media.availability !== 'unavailable' && media.availability !== 'damaged' && media.condition !== 'poor' && media.condition !== 'damaged') {
      continue;
    }
    const spatial = resolveMediaSpatialEntity(media, entities, profile.floorplanZones);
    issues.push({
      id: `media-${media.id}`,
      title: media.formatLabel,
      detail: `Condition: ${media.condition}`,
      locationLabel: spatial?.label ?? media.locationLabel,
      severity: media.availability === 'damaged' ? 'critical' : 'warning',
      spatialEntity: spatial,
    });
  }

  return issues;
}

export function buildRetailerSummaries(profile: StoreProfileDetail, asOf: Date | null): RetailerSummaryCard[] {
  const campaigns = filterCampaignsByDate(profile.campaigns, asOf);
  const activeCampaigns = campaigns.filter((campaign) => campaign.phase === 'current');
  const distinctActive = new Set(activeCampaigns.map((campaign) => campaign.campaignId)).size;
  const totalCampaigns = profile.campaignsToDateCount
    ?? new Set(profile.campaigns.map((campaign) => campaign.campaignId)).size;
  const issues = countOpenIssues(profile);
  const latestVisitDate = profile.lastSurveyDate
    ?? profile.walkthroughs.map((walk) => walk.captureDate).sort().slice(-1)[0]
    ?? null;
  const latestVisitTitle = profile.surveys.length
    ? profile.surveys.sort((a, b) => b.surveyDate.localeCompare(a.surveyDate))[0]?.title ?? 'Latest visit'
    : profile.walkthroughs[0]?.title ?? 'Latest walk-through';

  return [
    {
      id: 'campaigns',
      title: 'Active campaigns',
      value: String(distinctActive),
      detail: distinctActive > 0
        ? (activeCampaigns.length === 1 ? '1 installation live in store' : `${activeCampaigns.length} installations live in store`)
        : totalCampaigns > 0
          ? `${totalCampaigns} campaigns to date — browse campaign activity`
          : 'No campaign history yet',
      tone: distinctActive > 0 ? 'positive' : 'neutral',
    },
    {
      id: 'issues',
      title: 'Issues requiring attention',
      value: String(issues.length),
      detail: issues.length === 0 ? 'No open compliance or damage issues' : `${issues.length} item${issues.length === 1 ? '' : 's'} to review`,
      tone: issues.length > 0 ? 'warning' : 'positive',
    },
    {
      id: 'visit',
      title: 'Latest verified visit',
      value: latestVisitDate ?? '—',
      detail: latestVisitTitle,
      tone: latestVisitDate ? 'neutral' : 'warning',
    },
  ];
}

export function buildHistoryEntries(profile: StoreProfileDetail): HistoryEntry[] {
  const entries: HistoryEntry[] = [];

  for (const survey of profile.surveys) {
    entries.push({
      id: `survey-${survey.id}`,
      date: survey.surveyDate,
      title: survey.title,
      description: survey.notes ?? `Survey ${survey.status}`,
      kind: 'visit',
    });
  }
  for (const walk of profile.walkthroughs) {
    entries.push({
      id: `walk-${walk.id}`,
      date: walk.captureDate,
      title: walk.title,
      description: `Walk-through captured by ${walk.capturedBy}`,
      kind: 'evidence',
    });
  }
  for (const campaign of profile.campaigns.filter(isRetailerCampaign)) {
    entries.push({
      id: `campaign-${campaign.id}`,
      date: campaign.installationDate,
      title: campaign.campaignName,
      description: `${campaign.formatLabel} · ${campaign.phase}`,
      kind: 'campaign',
    });
  }
  for (const event of profile.historyEvents) {
    entries.push({
      id: event.id,
      date: event.date,
      title: event.title,
      description: event.description,
      kind: 'change',
    });
  }

  return entries
    .filter((entry) => entry.date && entry.date !== '—')
    .sort((a, b) => b.date.localeCompare(a.date));
}

export function buildMapPoints(
  profile: StoreProfileDetail,
  asOf: Date | null,
  layers: Record<'campaigns' | 'photos' | 'videos' | 'media' | 'issues', boolean>,
): MapPoint[] {
  const entities = buildSpatialEntities(profile);
  const points: MapPoint[] = [];
  const campaigns = filterCampaignsByDate(profile.campaigns, asOf).filter(
    isRetailerCampaign,
  );

  if (layers.campaigns) {
    const reference = asOf ?? new Date();
    const viewingHistorical = asOf != null && !isSameDay(reference, new Date());
    for (const campaign of campaigns) {
      const visibleOnMap = campaign.phase === 'current'
        || (viewingHistorical && campaign.phase === 'previous');
      if (!visibleOnMap) continue;
      const spatial = resolveCampaignSpatialEntity(campaign, entities, profile.floorplanZones, profile.aisles);
      if (!spatial) continue;
      points.push({
        id: `campaign-${campaign.id}`,
        kind: 'campaign',
        x: spatial.centerX,
        y: spatial.centerY,
        label: campaign.campaignName,
        spatialEntityId: spatial.id,
        spatialEntityKind: spatial.kind,
        previewImageUrl: campaign.installationImageUrls[0] ?? null,
      });
    }
  }

  if (layers.media) {
    for (const media of profile.mediaLocations) {
      const spatial = resolveMediaSpatialEntity(media, entities, profile.floorplanZones);
      // Store capacity is inventory, not a surveyed point. Never plot the
      // mapper's fallback grid coordinates as if they were real locations.
      if (!spatial) continue;
      points.push({
        id: `media-${media.id}`,
        kind: 'media',
        x: spatial.centerX,
        y: spatial.centerY,
        label: media.formatLabel,
        spatialEntityId: spatial.id,
        spatialEntityKind: spatial.kind,
      });
    }
  }

  if (layers.photos) {
    for (const image of profile.images.filter((item) => !item.hidden)) {
      const spatial = resolveImageSpatialEntity(image, entities, profile.floorplanZones);
      if (!spatial) continue;
      points.push({
        id: `photo-${image.id}`,
        kind: 'photo',
        x: spatial.centerX,
        y: spatial.centerY,
        label: image.caption,
        spatialEntityId: spatial.id,
        spatialEntityKind: spatial.kind,
        previewImageUrl: resolveEvidenceImageUrl(image),
      });
    }
  }

  if (layers.videos) {
    for (const walk of profile.walkthroughs) {
      const spatialEntities = resolveWalkthroughSpatialEntities(walk, entities, profile.floorplanZones);
      for (const spatial of spatialEntities) {
        points.push({
          id: `video-${walk.id}-${spatial.id}`,
          kind: 'video',
          x: spatial.centerX,
          y: spatial.centerY,
          label: walk.title,
          spatialEntityId: spatial.id,
          spatialEntityKind: spatial.kind,
          previewImageUrl: walk.thumbnailUrl || null,
        });
      }
    }
  }

  if (layers.issues) {
    for (const issue of countOpenIssues(profile)) {
      if (!issue.spatialEntity) continue;
      points.push({
        id: issue.id,
        kind: 'issue',
        x: issue.spatialEntity.centerX,
        y: issue.spatialEntity.centerY,
        label: issue.title,
        spatialEntityId: issue.spatialEntity.id,
        spatialEntityKind: issue.spatialEntity.kind,
      });
    }
  }

  return clusterAndLayoutMapPoints(points);
}

const POINT_KIND_ORDER: MapPoint['kind'][] = [
  'campaign',
  'photo',
  'video',
  'media',
  'issue',
];

const POINT_KIND_PLURALS: Record<MapPoint['kind'], string> = {
  campaign: 'campaigns',
  photo: 'photos',
  video: 'videos',
  media: 'media locations',
  issue: 'issues',
};

/**
 * Collapse repeated evidence at a location, then fan the distinct marker types
 * around it so photo, video and campaign points stay independently clickable.
 */
export function clusterAndLayoutMapPoints(points: MapPoint[]): MapPoint[] {
  const clusters = new Map<string, MapPoint>();

  for (const point of points) {
    const key = [
      point.spatialEntityKind,
      point.spatialEntityId,
      point.kind,
    ].join(':');
    const existing = clusters.get(key);

    if (!existing) {
      clusters.set(key, { ...point, count: 1 });
      continue;
    }

    const count = (existing.count ?? 1) + 1;
    clusters.set(key, {
      ...existing,
      count,
      label: `${count} ${POINT_KIND_PLURALS[point.kind]}`,
      previewImageUrl:
        existing.previewImageUrl || point.previewImageUrl || null,
    });
  }

  const byLocation = new Map<string, MapPoint[]>();
  for (const point of clusters.values()) {
    const key = `${point.spatialEntityKind}:${point.spatialEntityId}`;
    const locationPoints = byLocation.get(key) ?? [];
    locationPoints.push(point);
    byLocation.set(key, locationPoints);
  }

  const laidOut: MapPoint[] = [];
  for (const locationPoints of byLocation.values()) {
    const sorted = [...locationPoints].sort(
      (a, b) =>
        POINT_KIND_ORDER.indexOf(a.kind) - POINT_KIND_ORDER.indexOf(b.kind),
    );

    if (sorted.length === 1) {
      laidOut.push(sorted[0]!);
      continue;
    }

    sorted.forEach((point, index) => {
      const angle = -Math.PI / 2 + (index * Math.PI * 2) / sorted.length;
      const radius = 2.25;
      laidOut.push({
        ...point,
        x: Math.min(98, Math.max(2, point.x + Math.cos(angle) * radius)),
        y: Math.min(98, Math.max(2, point.y + Math.sin(angle) * radius)),
      });
    });
  }

  return laidOut;
}

export function getContextForSelection(
  profile: StoreProfileDetail,
  selection: { kind: SpatialEntity['kind']; id: string } | null,
  asOf: Date | null,
): {
  locationName: string;
  campaigns: CampaignPlacement[];
  evidenceImages: StoreImage[];
  mediaLocations: StoreProfileDetail['mediaLocations'];
  issues: RetailerIssue[];
  walkthroughs: StoreProfileDetail['walkthroughs'];
  previousEvidence: StoreImage[];
} | null {
  if (!selection) return null;

  const entities = buildSpatialEntities(profile);
  const entity = findEntity(entities, selection);
  if (!entity) return null;

  const zone = selection.kind === 'zone'
    ? profile.floorplanZones.find((item) => item.id === selection.id)
    : undefined;
  const aisle = selection.kind === 'aisle'
    ? profile.aisles.find((item) => item.id === selection.id)
    : undefined;
  const instoreLocationId = zone?.instoreLocationId ?? null;

  const campaigns = filterCampaignsByDate(profile.campaigns, asOf).filter((campaign) => {
    if (selection.kind === 'zone') {
      return campaign.floorplanZoneId === selection.id
        || (instoreLocationId != null && campaign.instoreLocationId === instoreLocationId);
    }
    return campaign.aisleId === selection.id;
  });

  const evidenceImages = instoreLocationId != null
    ? getInstallImagesForInstoreLocation(
      profile.images,
      instoreLocationId,
      zone?.id ?? null,
      campaigns,
    ).map((image) => ({ ...image, url: resolveEvidenceImageUrl(image) }))
    : profile.images
      .filter((image) => {
        if (selection.kind === 'aisle') return image.floorplanAisleId === selection.id;
        return image.floorplanZoneId === selection.id;
      })
      .map((image) => ({ ...image, url: resolveEvidenceImageUrl(image) }));

  const mediaLocations = profile.mediaLocations.filter((media) => {
    if (selection.kind === 'zone') {
      return media.floorplanZoneId === selection.id;
    }
    return media.floorplanAisleId === selection.id;
  });

  const issues = countOpenIssues(profile).filter((issue) =>
    issue.spatialEntity?.id === entity.id && issue.spatialEntity.kind === entity.kind,
  );

  const walkthroughs = profile.walkthroughs.filter((walk) => {
    const linked = resolveWalkthroughSpatialEntities(walk, entities, profile.floorplanZones);
    return linked.some((item) => item.id === entity.id && item.kind === entity.kind);
  });

  const previousEvidence = profile.images
    .filter((image) => {
      if (selection.kind === 'aisle') return image.floorplanAisleId === selection.id;
      return image.floorplanZoneId === selection.id;
    })
    .filter((image) => !evidenceImages.some((current) => current.id === image.id))
    .map((image) => ({ ...image, url: resolveEvidenceImageUrl(image) }));

  return {
    locationName: entity.label,
    campaigns,
    evidenceImages,
    mediaLocations,
    issues,
    walkthroughs,
    previousEvidence,
  };
}

export type RetailerLocationContext = NonNullable<ReturnType<typeof getContextForSelection>>;

function contextFromSpatialSelection(
  profile: StoreProfileDetail,
  selection: { kind: SpatialEntity['kind']; id: string },
  asOf: Date | null,
): RetailerLocationContext | null {
  return getContextForSelection(profile, selection, asOf);
}

export function getContextForMapPoint(
  profile: StoreProfileDetail,
  point: MapPoint,
  asOf: Date | null,
): RetailerLocationContext | null {
  const fromSelection = contextFromSpatialSelection(
    profile,
    { kind: point.spatialEntityKind, id: point.spatialEntityId },
    asOf,
  );
  if (fromSelection) return fromSelection;

  switch (point.kind) {
    case 'photo': {
      const imageId = point.id.startsWith('photo-') ? point.id.slice('photo-'.length) : point.id;
      const image = profile.images.find((item) => item.id === imageId && !item.hidden);
      if (!image) return null;
      const mapped = { ...image, url: resolveEvidenceImageUrl(image) };
      return {
        locationName: image.floorplanZoneLabel ?? image.caption ?? point.label,
        campaigns: filterCampaignsByDate(profile.campaigns, asOf).filter((campaign) =>
          campaign.campaignTaskId != null && image.assignmentId === campaign.campaignTaskId
        ),
        evidenceImages: [mapped],
        mediaLocations: [],
        issues: [],
        walkthroughs: [],
        previousEvidence: [],
      };
    }
    case 'media': {
      const mediaId = point.id.startsWith('media-') ? point.id.slice('media-'.length) : point.id;
      const media = profile.mediaLocations.find((item) => item.id === mediaId);
      if (!media) return null;
      return {
        locationName: media.locationLabel || point.label,
        campaigns: [],
        evidenceImages: [],
        mediaLocations: [media],
        issues: [],
        walkthroughs: [],
        previousEvidence: [],
      };
    }
    case 'campaign': {
      const campaignId = point.id.startsWith('campaign-') ? point.id.slice('campaign-'.length) : point.id;
      const campaign = filterCampaignsByDate(profile.campaigns, asOf).find((item) => item.id === campaignId);
      if (!campaign) return null;
      const instoreLocationId = campaign.instoreLocationId;
      const evidenceImages = instoreLocationId != null
        ? getInstallImagesForInstoreLocation(
          profile.images,
          instoreLocationId,
          campaign.floorplanZoneId ?? null,
          [campaign],
        ).map((image) => ({ ...image, url: resolveEvidenceImageUrl(image) }))
        : profile.images
          .filter((image) => !image.hidden && image.assignmentId === campaign.campaignTaskId)
          .map((image) => ({ ...image, url: resolveEvidenceImageUrl(image) }));
      return {
        locationName: campaign.instoreLocationName ?? point.label,
        campaigns: [campaign],
        evidenceImages,
        mediaLocations: [],
        issues: countOpenIssues(profile).filter((issue) => issue.title === campaign.campaignName),
        walkthroughs: [],
        previousEvidence: [],
      };
    }
    case 'video': {
      const suffix = point.id.startsWith('video-') ? point.id.slice('video-'.length) : point.id;
      const walk = profile.walkthroughs.find((item) => suffix.startsWith(`${item.id}-`) || suffix === item.id);
      if (!walk) return null;
      return {
        locationName: point.label || walk.title,
        campaigns: [],
        evidenceImages: [],
        mediaLocations: [],
        issues: [],
        walkthroughs: [walk],
        previousEvidence: [],
      };
    }
    case 'issue': {
      const issue = countOpenIssues(profile).find((item) => item.id === point.id);
      if (!issue) return null;
      return {
        locationName: issue.locationLabel,
        campaigns: [],
        evidenceImages: [],
        mediaLocations: [],
        issues: [issue],
        walkthroughs: [],
        previousEvidence: [],
      };
    }
    default: {
      const _exhaustive: never = point.kind;
      return _exhaustive;
    }
  }
}

function findEntity(
  entities: SpatialEntity[],
  selection: { kind: SpatialEntity['kind']; id: string },
): SpatialEntity | undefined {
  return entities.find((entity) => entity.kind === selection.kind && entity.id === selection.id);
}

export const MAP_LAYER_LABELS = {
  campaigns: 'Campaigns',
  photos: 'Photos',
  videos: 'Videos',
  media: 'Media',
  issues: 'Issues',
} as const;

export const MAP_POINT_STYLES: Record<MapPoint['kind'], { fill: string; label: string }> = {
  campaign: { fill: '#2563eb', label: 'Campaign' },
  photo: { fill: '#059669', label: 'Photo' },
  video: { fill: '#7c3aed', label: 'Video' },
  media: { fill: '#0891b2', label: 'Media location' },
  issue: { fill: '#dc2626', label: 'Issue' },
};
